/**
 * F-27 built-in toolbar actions v1, registered into the toolbar-action
 * registry (docs/08 registries/):
 *
 *  - cells: bold and strike-through (F-17-marks over each selected cell's
 *    WHOLE text — the same per-range marks the script view and the grid's cell
 *    editor write, because a cell whose every character is bold IS a bold
 *    cell), background color (F-17-bg palette popover: clear + 8 pastel
 *    presets + validated custom hex + an OPACITY slider over the fill each
 *    cell already has), text color (F-17-color: auto + 8 ink presets + custom
 *    hex), and add-comment. Every one of them applies to EVERY selected cell
 *    in ONE transaction = one undo step, local origin.
 *  - elements: bring to front / send to back (single LWW `z` writes),
 *    delete, add-comment.
 *
 * Add-comment delegates through the registry's `requestAddComment` hook —
 * a documented no-op (button disabled) until the comments feature binds it.
 */
import { useMemo, useState } from "react";

import { OPACITY_MAX, OPACITY_MIN, normalizeHexColor, themedFill } from "../../core/color.js";
import { cellShareLink, copyToClipboard, elementShareLink } from "../../core/share-link.js";
import { useThemeColors } from "../../core/useThemeColors.js";
import type { BoardSession } from "../../core/session.js";
import {
  registerToolbarAction,
  requestAddComment,
  isAddCommentBound,
  type CellsToolbarSelection,
  type ToolbarActionContext,
} from "../../registries/toolbar-actions/index.js";
import {
  BG_PRESETS,
  TEXT_PRESETS,
  opacityPreview,
  swatchPreview,
  type OpacityPreview,
  type SwatchPreview,
} from "./palette.js";
import { iconLink } from "../../ui/icons.js";
import { showToast } from "../../ui/toast.js";

/* ------------------------------------------------------------------ */
/* Shared selection plumbing (cells)                                   */
/* ------------------------------------------------------------------ */

/** Every selected cell, in range order — what the cell actions write to. */
function cellsOf(sel: CellsToolbarSelection): { rowId: string; colId: string }[] {
  const cells: { rowId: string; colId: string }[] = [];
  for (const rowId of sel.rowIds) {
    for (const colId of sel.colIds) cells.push({ rowId, colId });
  }
  return cells;
}

/** Per-cell backgrounds of the selection, in range order (for the previews). */
function collectBgs(session: BoardSession, sel: CellsToolbarSelection): (string | undefined)[] {
  return cellsOf(sel).map(({ rowId, colId }) => session.board.getCellBg(sel.sheetId, rowId, colId));
}

/** Per-cell TEXT colors of the selection — `undefined` = automatic ink. */
function collectColors(session: BoardSession, sel: CellsToolbarSelection): (string | undefined)[] {
  return cellsOf(sel).map(({ rowId, colId }) =>
    session.board.getCellColor(sel.sheetId, rowId, colId),
  );
}

/**
 * Read the selection's colors once per published anchor. The grid republishes
 * on every cells-map change, so these fold the LIVE values — recomputed when
 * the selection moves or its content changes, not on every pan/zoom
 * re-render.
 */
function useCellsPreview<T>(
  ctx: ToolbarActionContext,
  fold: (session: BoardSession, sel: CellsToolbarSelection) => T,
  empty: T,
): T {
  const sel = ctx.selection;
  // `fold`/`empty` are module-scope constants at every call site, so what
  // actually moves this is the selection.
  return useMemo(
    () => (sel.kind === "cells" ? fold(ctx.session, sel) : empty),
    [ctx.session, sel, fold, empty],
  );
}

const NO_SWATCH: SwatchPreview = { kind: "none" };
const NO_OPACITY: OpacityPreview = { kind: "none" };

const foldBgSwatch = (s: BoardSession, sel: CellsToolbarSelection): SwatchPreview =>
  swatchPreview(collectBgs(s, sel));
const foldBgOpacity = (s: BoardSession, sel: CellsToolbarSelection): OpacityPreview =>
  opacityPreview(collectBgs(s, sel));
const foldTextSwatch = (s: BoardSession, sel: CellsToolbarSelection): SwatchPreview =>
  swatchPreview(collectColors(s, sel));

/**
 * A hex field that only writes what `normalizeHexColor` accepts. Shared by
 * the fill and text-color popovers — both write a raw string into the CRDT,
 * so both need the same gate.
 */
function HexRow({
  current,
  onApply,
  label,
}: {
  current: string | undefined;
  onApply: (color: string) => void;
  label: string;
}) {
  const [hex, setHex] = useState("");
  const [invalid, setInvalid] = useState(false);

  const applyHex = () => {
    const normalized = normalizeHexColor(hex);
    if (normalized === null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    onApply(normalized);
  };

  return (
    <div className="bg-hex-row">
      <input
        className={`bg-hex-input${invalid ? " invalid" : ""}`}
        type="text"
        value={hex}
        placeholder={current ?? "#rrggbb"}
        spellCheck={false}
        aria-label={label}
        aria-invalid={invalid}
        onChange={(e) => {
          setHex(e.target.value);
          if (invalid) setInvalid(false);
        }}
        onKeyDown={(e) => {
          // Enter applies; everything else must not reach grid shortcuts.
          e.stopPropagation();
          if (e.key === "Enter") {
            e.preventDefault();
            applyHex();
          }
        }}
      />
      <button type="button" className="btn bg-hex-apply" onClick={applyHex}>
        Set
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Background color + opacity (cells)                                  */
/* ------------------------------------------------------------------ */

/** Apply `color` (undefined = clear) to every selected cell — ONE undo step. */
function applyBg(ctx: ToolbarActionContext, color: string | undefined): void {
  if (ctx.selection.kind !== "cells") return;
  ctx.session.setCellsBg(ctx.selection.sheetId, cellsOf(ctx.selection), color);
}

/** The pill button's swatch: current color, crossed "none", or neutral mixed. */
function BgSwatchIcon({ ctx }: { ctx: ToolbarActionContext }) {
  const preview = useCellsPreview(ctx, foldBgSwatch, NO_SWATCH);
  if (preview.kind === "color") {
    // Over the checkerboard, so a fill at 30% reads as a fill at 30% rather
    // than as a paler color.
    return (
      <span className="st-swatch-preview alpha">
        <span className="st-swatch-fill" style={{ background: preview.color }} />
      </span>
    );
  }
  return <span className={`st-swatch-preview ${preview.kind}`} />;
}

/**
 * F-17-bg opacity slider. Drags are LOCAL until released, then committed as
 * one transaction: a range input fires per pixel of travel, and writing each
 * one would be a CRDT update per pixel to every peer, and an undo step per
 * pixel to the user.
 *
 * Disabled with nothing filled — opacity is a property OF a fill, so there
 * would be nothing for it to act on (see session.setCellsBgOpacity).
 */
function BgOpacityRow({ ctx }: { ctx: ToolbarActionContext }) {
  const opacity = useCellsPreview(ctx, foldBgOpacity, NO_OPACITY);
  const [draft, setDraft] = useState<number | null>(null);
  const committed = opacity.kind === "value" ? opacity.pct : OPACITY_MAX;
  const pct = draft ?? committed;

  const commit = () => {
    if (draft === null) return;
    setDraft(null);
    if (draft !== committed && ctx.selection.kind === "cells") {
      ctx.session.setCellsBgOpacity(ctx.selection.sheetId, cellsOf(ctx.selection), draft);
    }
  };

  return (
    <label className="bg-opacity-row" title="Fill opacity">
      <span className="bg-opacity-label">Opacity</span>
      <input
        className="bg-opacity-slider"
        type="range"
        min={OPACITY_MIN}
        max={OPACITY_MAX}
        step={1}
        value={pct}
        disabled={opacity.kind === "none"}
        aria-label="Fill opacity (percent)"
        onChange={(e) => setDraft(Number(e.target.value))}
        onKeyDown={(e) => e.stopPropagation()} // arrows must not pan the grid
        onPointerUp={commit}
        onPointerCancel={commit}
        onKeyUp={commit}
        onBlur={commit}
      />
      <span className="bg-opacity-value">
        {opacity.kind === "mixed" && draft === null ? "—" : `${pct}%`}
      </span>
    </label>
  );
}

/** F-17-bg palette popover: clear + presets + opacity + custom hex. */
function BgPalette({ ctx }: { ctx: ToolbarActionContext }) {
  const preview = useCellsPreview(ctx, foldBgSwatch, NO_SWATCH);
  const current = preview.kind === "color" ? preview.color : undefined;
  const { surface } = useThemeColors();
  // Preview what the board will PAINT, not what gets stored: in dark mode the
  // fill is re-rendered into its own hue at a dark lightness (F-122,
  // core/color.ts `themedFill`), and a picker showing the raw pastel would be
  // advertising a color no cell on the board ever displays.
  const shown = (color: string): string => themedFill(color, surface) ?? color;

  return (
    <div className="bg-palette" role="group" aria-label="Background color">
      <div className="bg-palette-swatches">
        <button
          type="button"
          className={`bg-swatch bg-swatch-none${preview.kind === "none" ? " selected" : ""}`}
          data-tip="No fill"
          aria-label="No fill"
          onClick={() => applyBg(ctx, undefined)}
        />
        {BG_PRESETS.map((color) => (
          <button
            key={color}
            type="button"
            className={`bg-swatch${current === color ? " selected" : ""}`}
            style={{ background: shown(color) }}
            data-tip={color}
            aria-label={color}
            onClick={() => applyBg(ctx, color)}
          />
        ))}
      </div>
      {/* A preset click re-opaques the fill (presets are 6-digit), so the
          slider sits BELOW them: pick the color, then fade it. */}
      <BgOpacityRow ctx={ctx} />
      <HexRow current={current} onApply={(c) => applyBg(ctx, c)} label="Custom hex color" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Text color (cells)                                                  */
/* ------------------------------------------------------------------ */

/** Apply `color` (undefined = back to automatic ink) — ONE undo step. */
function applyTextColor(ctx: ToolbarActionContext, color: string | undefined): void {
  if (ctx.selection.kind !== "cells") return;
  ctx.session.setCellsColor(ctx.selection.sheetId, cellsOf(ctx.selection), color);
}

/** The pill button: an "A" over a bar in the selection's current ink. */
function TextColorIcon({ ctx }: { ctx: ToolbarActionContext }) {
  const preview = useCellsPreview(ctx, foldTextSwatch, NO_SWATCH);
  return (
    <span className="st-ink-preview" aria-hidden="true">
      <span className="st-ink-glyph">A</span>
      <span
        className={`st-ink-bar${preview.kind === "color" ? "" : ` ${preview.kind}`}`}
        {...(preview.kind === "color" ? { style: { background: preview.color } } : {})}
      />
    </span>
  );
}

/**
 * F-17-color popover: Auto + ink presets + custom hex.
 *
 * "Auto" is the default and clears the key rather than writing a color —
 * an auto cell tracks its own fill and the reader's theme (core/color.ts
 * `readableTextColor`), which is what keeps a highlighted column legible in
 * dark mode. An explicit color opts out of that and is honored verbatim, so
 * the popover says as much next to the swatches.
 */
function TextColorPalette({ ctx }: { ctx: ToolbarActionContext }) {
  const preview = useCellsPreview(ctx, foldTextSwatch, NO_SWATCH);
  const current = preview.kind === "color" ? preview.color : undefined;

  return (
    <div className="bg-palette" role="group" aria-label="Text color">
      <div className="bg-palette-swatches">
        <button
          type="button"
          className={`bg-swatch bg-swatch-auto${preview.kind === "none" ? " selected" : ""}`}
          data-tip="Automatic — stays readable on the cell's fill, in either theme"
          aria-label="Automatic text color"
          onClick={() => applyTextColor(ctx, undefined)}
        />
        {TEXT_PRESETS.map((color) => (
          <button
            key={color}
            type="button"
            className={`bg-swatch${current === color ? " selected" : ""}`}
            style={{ background: color }}
            data-tip={color}
            aria-label={color}
            onClick={() => applyTextColor(ctx, color)}
          />
        ))}
      </div>
      <HexRow current={current} onApply={(c) => applyTextColor(ctx, c)} label="Custom text color" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Bold / strike (cells) — F-17-marks over each cell's whole text       */
/* ------------------------------------------------------------------ */

const foldBold = (s: BoardSession, sel: CellsToolbarSelection): boolean =>
  s.cellsFullyMarked(sel.sheetId, cellsOf(sel), "bold");
const foldStrike = (s: BoardSession, sel: CellsToolbarSelection): boolean =>
  s.cellsFullyMarked(sel.sheetId, cellsOf(sel), "strike");

/**
 * A letter that shows the mark it applies AND whether the selection already
 * carries it — the pill has no pressed state of its own (`.st-btn.active` is
 * "this button's popover is open"), and a toggle that never looks pressed is
 * a toggle nobody trusts.
 */
function MarkToggleIcon({
  ctx,
  kind,
}: {
  ctx: ToolbarActionContext;
  kind: "bold" | "strike";
}) {
  const on = useCellsPreview(ctx, kind === "bold" ? foldBold : foldStrike, false);
  return (
    <span className={`st-mark-glyph ${kind}${on ? " on" : ""}`} aria-hidden="true">
      {kind === "bold" ? "B" : "S"}
    </span>
  );
}

function applyCellsMark(ctx: ToolbarActionContext, kind: "bold" | "strike"): void {
  if (ctx.selection.kind !== "cells") return;
  ctx.session.toggleCellsMark(ctx.selection.sheetId, cellsOf(ctx.selection), kind);
}

/* ------------------------------------------------------------------ */
/* Icons (16 px inline SVG, stroke = currentColor)                     */
/* ------------------------------------------------------------------ */

const svgProps = {
  width: 16,
  height: 16,
  viewBox: "0 0 16 16",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

const iconBringFront = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M10.5 4V3a.9.9 0 0 0-.9-.9H3a.9.9 0 0 0-.9.9v6.6a.9.9 0 0 0 .9.9h1" />
    <rect x="5.5" y="5.5" width="8" height="8" rx="1" fill="currentColor" stroke="none" />
  </svg>
);

const iconSendBack = (
  <svg {...svgProps} aria-hidden="true">
    <rect
      x="2.5"
      y="2.5"
      width="8"
      height="8"
      rx="1"
      fill="currentColor"
      stroke="none"
      opacity="0.35"
    />
    <rect x="5.5" y="5.5" width="8" height="8" rx="1" />
  </svg>
);

const iconComment = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M2.5 4.5A1.5 1.5 0 0 1 4 3h8a1.5 1.5 0 0 1 1.5 1.5v4.6A1.5 1.5 0 0 1 12 10.6H7.4l-2.9 2.9v-2.9H4a1.5 1.5 0 0 1-1.5-1.5V4.5z" />
  </svg>
);

const iconDelete = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M2.5 4h11M5.5 4V3a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v1M4.2 4l.6 8.6a1 1 0 0 0 1 .9h4.4a1 1 0 0 0 1-.9L11.8 4M6.5 7v4M9.5 7v4" />
  </svg>
);

/* ------------------------------------------------------------------ */
/* Registration (module scope; re-import under HMR replaces by id)     */
/* ------------------------------------------------------------------ */

registerToolbarAction({
  id: "cell-bold",
  appliesTo: "cells",
  tooltip: "Bold (⌘B)",
  order: 8,
  icon: (ctx) => <MarkToggleIcon ctx={ctx} kind="bold" />,
  run: (ctx) => applyCellsMark(ctx, "bold"),
});

registerToolbarAction({
  id: "cell-strike",
  appliesTo: "cells",
  tooltip: "Strikethrough (⌘⇧X)",
  order: 9,
  icon: (ctx) => <MarkToggleIcon ctx={ctx} kind="strike" />,
  run: (ctx) => applyCellsMark(ctx, "strike"),
});

registerToolbarAction({
  id: "cell-bg",
  appliesTo: "cells",
  tooltip: "Background color",
  order: 10,
  icon: (ctx) => <BgSwatchIcon ctx={ctx} />,
  popover: (ctx) => <BgPalette ctx={ctx} />,
});

registerToolbarAction({
  id: "cell-text-color",
  appliesTo: "cells",
  tooltip: "Text color",
  order: 11,
  icon: (ctx) => <TextColorIcon ctx={ctx} />,
  popover: (ctx) => <TextColorPalette ctx={ctx} />,
});

registerToolbarAction({
  id: "element-bring-front",
  appliesTo: "elements",
  tooltip: "Bring to front",
  order: 10,
  icon: iconBringFront,
  run: (ctx) => {
    if (ctx.selection.kind === "element") ctx.session.bringElementToFront(ctx.selection.elementId);
  },
});

registerToolbarAction({
  id: "element-send-back",
  appliesTo: "elements",
  tooltip: "Send to back",
  order: 11,
  icon: iconSendBack,
  run: (ctx) => {
    if (ctx.selection.kind === "element") ctx.session.sendElementToBack(ctx.selection.elementId);
  },
});

registerToolbarAction({
  id: "add-comment",
  appliesTo: "both",
  tooltip: "Add comment",
  order: 50,
  icon: iconComment,
  // Disabled (not hidden) until the comments feature binds the hook — the
  // affordance is discoverable and flips live once bound.
  enabled: () => isAddCommentBound(),
  run: (ctx) => {
    const sel = ctx.selection;
    if (sel.kind === "cells") {
      requestAddComment(ctx.session, {
        kind: "cell",
        sheetId: sel.sheetId,
        rowId: sel.anchor.rowId,
        colId: sel.anchor.colId,
        rect: sel.anchor.rect,
      });
    } else {
      requestAddComment(ctx.session, {
        kind: "element",
        elementId: sel.elementId,
        rect: sel.rect,
      });
    }
  },
});

/**
 * F-123 "copy link to this": a link that opens the board panned and zoomed
 * onto the selection — its ANCHOR cell for a range (one link names one
 * thing), the element for an element — and selects it. Same link as the
 * context menus' "Copy link", so the two entry points can never differ.
 */
registerToolbarAction({
  id: "copy-link",
  appliesTo: "both",
  tooltip: (ctx) =>
    ctx.selection.kind === "cells" ? "Copy link to this cell" : "Copy link to this item",
  order: 60,
  icon: iconLink,
  run: (ctx) => {
    const sel = ctx.selection;
    const link =
      sel.kind === "cells"
        ? cellShareLink(ctx.session.boardId, sel.anchor.rowId, sel.anchor.colId)
        : elementShareLink(ctx.session.boardId, sel.elementId);
    void copyToClipboard(link).then((copied) =>
      showToast(
        copied
          ? sel.kind === "cells"
            ? "Link to this cell copied"
            : "Link to this item copied"
          : "Couldn't reach the clipboard",
      ),
    );
  },
});

registerToolbarAction({
  id: "element-delete",
  appliesTo: "elements",
  tooltip: "Delete",
  order: 90,
  icon: iconDelete,
  run: (ctx) => {
    // The canvas shell observes the element vanishing and clears selection.
    if (ctx.selection.kind === "element") ctx.session.deleteElement(ctx.selection.elementId);
  },
});
